/**
 * PushPermissionPrompt
 * -------------------------------------------------------------------
 * Banner compacto que solicita permissão de push.
 *  - Android/Desktop: pergunta direto via Notification.requestPermission
 *  - iOS com PWA instalada (standalone): idem
 *
 * Persistência da decisão:
 *  - Fonte de verdade é `notification_preferences.prompt_response` no DB
 *    (cross-device, cross-session). Setada via `push.recordPromptDecision`.
 *  - localStorage segue como fallback offline e para evitar flash do banner
 *    no carregamento antes do hook ler o DB.
 */

import { useCallback, useEffect, useState } from "react";
import { Bell, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext.hook";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { useToast } from "@/hooks/use-toast";

const DISMISS_KEY = "bravaforte:push_prompt_dismissed_at";
const DISMISS_WINDOW_MS = 3 * 24 * 60 * 60 * 1000; // 3 dias
// Atraso entre login completo e exibição do banner. Evita que ele apareça
// "atravessando" a tela de auth/loading antes do contexto estar pronto.
const SHOW_DELAY_MS = 10_000;

function isRecentlyDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    return Number.isFinite(ts) && Date.now() - ts < DISMISS_WINDOW_MS;
  } catch {
    return false;
  }
}

export const PushPermissionPrompt = () => {
  const { user, loading: authLoading } = useAuth();
  const push = usePushNotifications();
  const { toast } = useToast();
  const [dismissed, setDismissed] = useState<boolean>(() => isRecentlyDismissed());
  const [delayElapsed, setDelayElapsed] = useState(false);

  // Inicia contagem de 10s só depois que auth concluiu e há user.
  // Reseta se o user trocar (logout → outra conta).
  useEffect(() => {
    if (authLoading || !user) {
      setDelayElapsed(false);
      return;
    }
    const t = setTimeout(() => setDelayElapsed(true), SHOW_DELAY_MS);
    return () => clearTimeout(t);
  }, [authLoading, user]);

  const persistLocalDismiss = useCallback(() => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      // ignore
    }
    setDismissed(true);
  }, []);

  const handleActivate = useCallback(async () => {
    try {
      const ok = await push.subscribe();
      if (ok) {
        // Subscribe deu certo → registrar 'accepted' (master + categorias true)
        await push.recordPromptDecision("accepted");
      } else {
        // Falhou: pode ser permission denied, dismiss do diálogo nativo, etc.
        // Em qualquer caso, o usuário decidiu não ativar agora — registra
        // como rejected para não bombardear o banner novamente.
        await push.recordPromptDecision("rejected");
        if (push.permission === "denied") {
          toast({
            title: "Permissão negada",
            description:
              "Ative manualmente nas configurações do seu navegador/SO.",
            variant: "destructive",
          });
        }
      }
    } finally {
      persistLocalDismiss();
    }
  }, [push, toast, persistLocalDismiss]);

  const handleDismiss = useCallback(async () => {
    try {
      await push.recordPromptDecision("rejected");
    } finally {
      persistLocalDismiss();
    }
  }, [push, persistLocalDismiss]);

  if (dismissed) return null;
  if (!user || authLoading) return null;
  if (!delayElapsed) return null;
  if (!push.canPromptNow) return null;

  return (
    <div className="fixed inset-x-0 bottom-20 md:bottom-0 z-[60] flex justify-center px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pointer-events-none">
      <Card className="pointer-events-auto w-full max-w-md border border-border bg-background p-4 shadow-lg">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Bell className="h-4 w-4" aria-hidden="true" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground">
              Ativar notificações
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Receba avisos quando houver novas mensagens.
            </p>
            <div className="mt-3 flex gap-2">
              <Button
                size="sm"
                variant="primary"
                onClick={handleActivate}
                disabled={push.isBusy}
              >
                {push.isBusy ? "Ativando..." : "Ativar notificações"}
              </Button>
              <Button size="sm" variant="ghost" onClick={handleDismiss}>
                Agora não
              </Button>
            </div>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Fechar aviso"
            className="shrink-0 rounded-md p-1 text-muted-foreground hover:bg-muted"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </Card>
    </div>
  );
};
